import React from 'react';
import { Route, IndexRoute, IndexRedirect, Redirect } from 'react-router';

import App from '../modules/x/App';

import { anonymous, authenticate, loadRoute, errorLoadingRoute } from './util';
import store from '../store';

export const createRedirect = (substr, next) => (
  { location: { pathname = '/', ...others } = {} } = {},
  replace
) => replace({ ...others, pathname: pathname.replace(substr, next) });

const redirectFromBeta = createRedirect(/^\/beta\/?/, '/');
const redirectFromX = createRedirect(/^\/x\/?/, '/');

const redirectLegacyRoutines = (
  // eslint-disable-next-line no-unused-vars
  { location: { pathname = '/', ...others } = {}, params: { runID } = {} } = {},
  replace
) => replace({ ...others, pathname: `/pushbots/${runID}` });

const redirectLegacyTasks = (
  {
    // eslint-disable-next-line no-unused-vars
    location: { pathname = '/', ...others } = {},
    params: { runID, taskName } = {}
  } = {},
  replace
) => replace({ ...others, pathname: `/pushbots/${runID}/tasks/${taskName}` });

const redirectLegacyTemplates = (
  // eslint-disable-next-line no-unused-vars
  { location: { pathname = '/', ...others } = {}, params: { runID } = {} } = {},
  replace
) => replace({ ...others, pathname: `/build/${runID}` });

export const redirectIfNotAdmin = (
  // eslint-disable-next-line no-unused-vars
  { location: { pathname = '/', ...others } = {}, params: { runID } = {} } = {},
  replace
) => {
  const session = store.getState().session;
  const { isTeamOwner, isTeamAdmin } = session;
  if (!isTeamOwner && !isTeamAdmin) replace({ ...others, pathname: '/' });
};

export default function BetaRouter(features = {}) {
  const {
    embeddedMode,
    manualReportingEnabled,
    timelineChart,
    webformsV2,
    workDoneBetterNavigation
  } = features;

  return (
    <Route>
      <Route path="beta*" onEnter={redirectFromBeta} />
      <Route path="x*" onEnter={redirectFromX} />
      <Route path="routines/:runID" onEnter={redirectLegacyRoutines} />
      <Route path="tasks/:runID/:taskName" onEnter={redirectLegacyTasks} />
      <Route path="templates/:runID" onEnter={redirectLegacyTemplates} />

      <Route onEnter={(next, replace, done) => anonymous(next, replace, done)}>
        <Route path="login">
          <IndexRoute
            getComponent={(next, done) => {
              System.import(
                /* webpackChunkName: "Login" */ '../modules/x/Login'
              )
                .then(loadRoute(done))
                .catch(errorLoadingRoute);
            }}
          />
          <Route
            path="forgot"
            getComponent={(next, done) => {
              System.import(
                /* webpackChunkName: "Forgot" */ '../modules/x/Login/Forgot'
              )
                .then(loadRoute(done))
                .catch(errorLoadingRoute);
            }}
          />
          <Route
            path="sudo"
            onEnter={(next, replace, done) =>
              anonymous(next, replace, done, true)
            }
            getComponent={(next, done) => {
              System.import(
                /* webpackChunkName: "Sudo" */ '../modules/x/Login/Sudo'
              )
                .then(loadRoute(done))
                .catch(errorLoadingRoute);
            }}
          />
        </Route>
      </Route>
      <Route
        path="deadbeef"
        getComponent={(next, done) => {
          System.import(
            /* webpackChunkName: "DeadBeef" */ '../modules/DeadBeef'
          )
            .then(loadRoute(done))
            .catch(errorLoadingRoute);
        }}
      />
      <Route
        path="invitation/:id"
        onEnter={({ location, params: { id } = {} }, replace) => {
          replace({
            pathname: '/join',
            query: { invitation: id },
            state: { referrer: location }
          });
        }}
      />
      <Route
        path="invites/:id"
        onEnter={({ location, params: { id } = {} }, replace) => {
          replace({
            pathname: '/join',
            query: { invitation: id },
            state: { referrer: location }
          });
        }}
      />
      <Route
        path="join"
        getComponent={(next, done) => {
          System.import(/* webpackChunkName: "Join" */ '../modules/x/Join')
            .then(loadRoute(done))
            .catch(errorLoadingRoute);
        }}
      />
      {webformsV2 && <Redirect path="forms/:name" to="form/:name" />}
      {['webforms/:id', 'forms/:webhookname'].map((path, i) => (
        <Route
          getComponent={(next, done) => {
            System.import(
              /* webpackChunkName: "WebformAlpha" */ '../modules/x/WebformAlpha'
            )
              .then(loadRoute(done))
              .catch(errorLoadingRoute);
          }}
          key={i}
          {...{ path }}
        />
      ))}

      <Route
        path="progress/:runID"
        getComponent={(next, done) => {
          System.import(
            /* webpackChunkName: "PushbotProgress" */ '../modules/x/PushbotProgress'
          )
            .then(loadRoute(done))
            .catch(errorLoadingRoute);
        }}
      />

      <Route component={App}>
        <Route
          path="/"
          onEnter={authenticate}
          onChange={(prev, next, replace, done) =>
            authenticate(next, replace, done)
          }
        >
          <IndexRedirect to="tasks" />
          <Redirect from="home" to="tasks" />
          {!embeddedMode && <Redirect from="processes" to="pushbots" />}
          <Route
            searchButton
            path="build"
            getComponent={(next, done) => {
              System.import(
                /* webpackChunkName: "Search" */ '../modules/x/Search'
              )
                .then(loadRoute(done))
                .catch(errorLoadingRoute);
            }}
          >
            <Route
              path="new"
              getComponent={(next, done) => {
                System.import(
                  /* webpackChunkName: "NewPushbot" */ '../modules/x/BuildPushbot/NewPushbot'
                )
                  .then(loadRoute(done))
                  .catch(errorLoadingRoute);
              }}
            />
          </Route>
          {embeddedMode && (
            <Route
              path="build/:processID/test"
              getComponent={(next, done) => {
                System.import(
                  /* webpackChunkName: "StartPushbot" */ '../modules/x/StartPushbot'
                )
                  .then(loadRoute(done))
                  .catch(errorLoadingRoute);
              }}
            />
          )}
          <Route detail path="build/:processID">
            <IndexRoute
              getComponent={(next, done) => {
                System.import(
                  /* webpackChunkName: "BuildPushbot" */ '../modules/x/BuildPushbot'
                )
                  .then(loadRoute(done))
                  .catch(errorLoadingRoute);
              }}
            />
            <Route
              getComponent={(next, done) => {
                System.import(
                  /* webpackChunkName: "BuildPushbot" */ '../modules/x/BuildPushbot'
                )
                  .then(loadRoute(done))
                  .catch(errorLoadingRoute);
              }}
            >
              <Route
                path="start"
                getComponent={(next, done) => {
                  System.import(
                    /* webpackChunkName: "StartPushbot" */ '../modules/x/StartPushbot'
                  )
                    .then(loadRoute(done))
                    .catch(errorLoadingRoute);
                }}
              />
              {!embeddedMode && (
                <Route
                  path="test"
                  getComponent={(next, done) => {
                    System.import(
                      /* webpackChunkName: "StartPushbot" */ '../modules/x/StartPushbot'
                    )
                      .then(loadRoute(done))
                      .catch(errorLoadingRoute);
                  }}
                />
              )}
            </Route>
            <Route
              path="fields"
              getComponent={(next, done) => {
                System.import(
                  /* webpackChunkName: "BuildPushbot" */ '../modules/x/BuildPushbot/Process/ListFields'
                )
                  .then(loadRoute(done))
                  .catch(errorLoadingRoute);
              }}
            />
            <Route
              path="fields/:fieldName"
              getComponent={(next, done) => {
                System.import(
                  /* webpackChunkName: "BuildPushbot" */ '../modules/x/BuildPushbot/Process/ConfigureField'
                )
                  .then(loadRoute(done))
                  .catch(errorLoadingRoute);
              }}
            />
            <Route
              appDrawer={false}
              detail={false}
              paper={false}
              path="flow"
              getComponent={(next, done) => {
                System.import(
                  /* webpackChunkName: "FlowChart" */ '../modules/x/FlowChart'
                )
                  .then(loadRoute(done))
                  .catch(errorLoadingRoute);
              }}
            />
            <Route
              path="tasks/:stepName"
              getComponent={(next, done) => {
                System.import(
                  /* webpackChunkName: "BuildPushbot" */ '../modules/x/BuildPushbot/Step/ConfigureStep'
                )
                  .then(loadRoute(done))
                  .catch(errorLoadingRoute);
              }}
            />
            <Route
              path="tasks/:stepName/test"
              getComponent={(next, done) => {
                System.import(
                  /* webpackChunkName: "BuildPushbot" */ '../modules/x/BuildPushbot/Step/ConfigureStep'
                )
                  .then(loadRoute(done))
                  .catch(errorLoadingRoute);
              }}
            />
            <Route
              path="tasks/:stepName/fields"
              getComponent={(next, done) => {
                System.import(
                  /* webpackChunkName: "BuildPushbot" */ '../modules/x/BuildPushbot/Step/ListFields'
                )
                  .then(loadRoute(done))
                  .catch(errorLoadingRoute);
              }}
            />
            <Route
              path="tasks/:stepName/fields/:fieldName"
              getComponent={(next, done) => {
                System.import(
                  /* webpackChunkName: "BuildPushbot" */ '../modules/x/BuildPushbot/Step/ConfigureField'
                )
                  .then(loadRoute(done))
                  .catch(errorLoadingRoute);
              }}
            />
            <Route
              path="tasks/:stepName/action/:actionName"
              getComponent={(next, done) => {
                System.import(
                  /* webpackChunkName: "BuildPushbot" */ '../modules/x/BuildPushbot/Step/ConfigureAction'
                )
                  .then(loadRoute(done))
                  .catch(errorLoadingRoute);
              }}
            />
            <Route
              path=":menu"
              getComponent={(next, done) => {
                System.import(
                  /* webpackChunkName: "BuildPushbot" */ '../modules/x/BuildPushbot'
                )
                  .then(loadRoute(done))
                  .catch(errorLoadingRoute);
              }}
            />
          </Route>

          <Route
            path="group/:groupName"
            getComponent={(next, done) => {
              System.import(
                /* webpackChunkName: "EditGroup" */ '../modules/x/Team/EditGroup'
              )
                .then(loadRoute(done))
                .catch(errorLoadingRoute);
            }}
          />
          <Route path="integrations">
            <IndexRoute
              getComponent={(next, done) => {
                System.import(
                  /* webpackChunkName: "IntegrationList" */ '../modules/x/IntegrationList'
                )
                  .then(loadRoute(done))
                  .catch(errorLoadingRoute);
              }}
              onEnter={redirectIfNotAdmin}
            />
            <Route
              detail
              path="add"
              getComponent={(next, done) => {
                System.import(
                  /* webpackChunkName: "IntegrationTypeChooser" */ '../modules/x/Integration/IntegrationTypeChooser'
                )
                  .then(loadRoute(done))
                  .catch(errorLoadingRoute);
              }}
              onEnter={redirectIfNotAdmin}
            />
            <Route
              detail
              path="add/:appName"
              getComponent={(next, done) => {
                System.import(
                  /* webpackChunkName: "EditIntegration" */ '../modules/x/Integration/EditIntegration'
                )
                  .then(loadRoute(done))
                  .catch(errorLoadingRoute);
              }}
              onEnter={redirectIfNotAdmin}
            />
            <Route
              detail
              path="edit/:username"
              getComponent={(next, done) => {
                System.import(
                  /* webpackChunkName: "EditIntegration" */ '../modules/x/Integration/EditIntegration'
                )
                  .then(loadRoute(done))
                  .catch(errorLoadingRoute);
              }}
              onEnter={redirectIfNotAdmin}
            />
            <Route
              detail
              path="app/:appName"
              getComponent={(next, done) => {
                System.import(
                  /* webpackChunkName: "IntegrationsOfTypeList" */ '../modules/x/Integration/IntegrationsOfTypeList'
                )
                  .then(loadRoute(done))
                  .catch(errorLoadingRoute);
              }}
              onEnter={redirectIfNotAdmin}
            />
          </Route>

          <Route
            path="notifications"
            getComponent={(next, done) => {
              System.import(
                /* webpackChunkName: "NotificationList" */ '../modules/x/NotificationList'
              )
                .then(loadRoute(done))
                .catch(errorLoadingRoute);
            }}
          />

          <Redirect path="profile" to="profile/me" />
          <Route
            detail
            path="profile/:username"
            getComponent={(next, done) => {
              System.import(
                /* webpackChunkName: "Profile" */ '../modules/x/Profile'
              )
                .then(loadRoute(done))
                .catch(errorLoadingRoute);
            }}
          >
            <Route
              path="password"
              getComponent={(next, done) => {
                System.import(
                  /* webpackChunkName: "Profile" */ '../modules/x/Profile/ChangePassword'
                )
                  .then(loadRoute(done))
                  .catch(errorLoadingRoute);
              }}
            />
            <Route
              path="photo"
              getComponent={(next, done) => {
                System.import(
                  /* webpackChunkName: "Profile" */ '../modules/x/Profile/ChangePhoto'
                )
                  .then(loadRoute(done))
                  .catch(errorLoadingRoute);
              }}
            />
            <Route
              path="phone"
              getComponent={(next, done) => {
                System.import(
                  /* webpackChunkName: "Profile" */ '../modules/x/Profile/PhoneVerification'
                )
                  .then(loadRoute(done))
                  .catch(errorLoadingRoute);
              }}
            />
            <Route
              path="email"
              getComponent={(next, done) => {
                System.import(
                  /* webpackChunkName: "Profile" */ '../modules/x/Profile/ChangeEmail'
                )
                  .then(loadRoute(done))
                  .catch(errorLoadingRoute);
              }}
            />
            <Route
              path="fullname"
              getComponent={(next, done) => {
                System.import(
                  /* webpackChunkName: "Profile" */ '../modules/x/Profile/ChangeFullName'
                )
                  .then(loadRoute(done))
                  .catch(errorLoadingRoute);
              }}
            />
            <Route
              path="permissions"
              getComponent={(next, done) => {
                System.import(
                  /* webpackChunkName: "Profile" */ '../modules/x/Profile/ChangePermissions'
                )
                  .then(loadRoute(done))
                  .catch(errorLoadingRoute);
              }}
            />
            <Route
              path="notifications"
              getComponent={(next, done) => {
                System.import(
                  /* webpackChunkName: "Profile" */ '../modules/x/NotificationPreferences'
                )
                  .then(loadRoute(done))
                  .catch(errorLoadingRoute);
              }}
            />
            <Route
              path="version"
              getComponent={(next, done) => {
                System.import(
                  /* webpackChunkName: "Profile" */ '../modules/x/Profile/ChangePushbotVersion'
                )
                  .then(loadRoute(done))
                  .catch(errorLoadingRoute);
              }}
            />
            <Route
              path="slack"
              getComponent={(next, done) => {
                System.import(
                  /* webpackChunkName: "Profile" */ '../modules/x/Profile/ChangeSlack'
                )
                  .then(loadRoute(done))
                  .catch(errorLoadingRoute);
              }}
            />
          </Route>

          <Route
            searchButton
            path="pushbots"
            getComponent={(next, done) => {
              System.import(
                /* webpackChunkName: "Search" */ '../modules/x/Search'
              )
                .then(loadRoute(done))
                .catch(errorLoadingRoute);
            }}
          />

          <Route
            path="pushbots/:runID"
            getComponent={(next, done) => {
              System.import(/* webpackChunkName: "Task" */ '../modules/x/Task')
                .then(loadRoute(done))
                .catch(errorLoadingRoute);
            }}
          >
            <Route
              path="abort"
              getComponent={(next, done) => {
                System.import(
                  /* webpackChunkName: "Task" */ '../modules/x/Task/RunAbort'
                )
                  .then(loadRoute(done))
                  .catch(errorLoadingRoute);
              }}
            />
            <Route
              path="comment"
              getComponent={(next, done) => {
                System.import(
                  /* webpackChunkName: "Task" */ '../modules/x/Task/Comments'
                )
                  .then(loadRoute(done))
                  .catch(errorLoadingRoute);
              }}
            />
            <Route path="tasks/:taskName">
              <Route
                path="reassign"
                getComponent={(next, done) => {
                  System.import(
                    /* webpackChunkName: "Task" */ '../modules/x/Task/Reassign'
                  )
                    .then(loadRoute(done))
                    .catch(errorLoadingRoute);
                }}
              />
              <Route
                path="reopen"
                getComponent={(next, done) => {
                  System.import(
                    /* webpackChunkName: "Task" */ '../modules/x/Task/Reopen'
                  )
                    .then(loadRoute(done))
                    .catch(errorLoadingRoute);
                }}
              />
              <Route
                path="snooze"
                getComponent={(next, done) => {
                  System.import(
                    /* webpackChunkName: "Task" */ '../modules/x/Task/Snooze'
                  )
                    .then(loadRoute(done))
                    .catch(errorLoadingRoute);
                }}
              />
            </Route>
          </Route>
          <Route
            detail
            path="pushbots/:runID/tasks/:taskName/details"
            getComponent={(next, done) => {
              System.import(
                /* webpackChunkName: "Task" */ '../modules/x/Task/Details'
              )
                .then(loadRoute(done))
                .catch(errorLoadingRoute);
            }}
          />
          <Route
            path="pushbots/:runID/tasks/:taskName/comments"
            getComponent={(next, done) => {
              System.import(
                /* webpackChunkName: "Task" */ '../modules/x/Task/Comments'
              )
                .then(loadRoute(done))
                .catch(errorLoadingRoute);
            }}
          />
          <Route
            detail
            path="pushbots/:runID/tasks/:taskName/wait"
            getComponent={(next, done) => {
              System.import(
                /* webpackChunkName: "Task" */ '../modules/x/Task/Wait'
              )
                .then(loadRoute(done))
                .catch(errorLoadingRoute);
            }}
          />

          {!manualReportingEnabled && <Redirect path="report" to="/data" />}
          <Route
            path="report"
            getComponent={(next, done) => {
              System.import(
                /* webpackChunkName: "Report" */ '../modules/x/Report'
              )
                .then(loadRoute(done))
                .catch(errorLoadingRoute);
            }}
          />

          <Route
            detail
            path="singleSignOn"
            getComponent={(next, done) => {
              System.import(
                /* webpackChunkName: "SingleSignOn" */ '../modules/x/SingleSignOn'
              )
                .then(loadRoute(done))
                .catch(errorLoadingRoute);
            }}
          />

          <Route
            searchButton
            path="start"
            getComponent={(next, done) => {
              System.import(
                /* webpackChunkName: "Search" */ '../modules/x/Search'
              )
                .then(loadRoute(done))
                .catch(errorLoadingRoute);
            }}
          >
            {!embeddedMode && (
              <Route
                path=":processID"
                getComponent={(next, done) => {
                  System.import(
                    /* webpackChunkName: "StartPushbot" */ '../modules/x/StartPushbot'
                  )
                    .then(loadRoute(done))
                    .catch(errorLoadingRoute);
                }}
              />
            )}
          </Route>
          {embeddedMode && (
            <Route
              path="start/:processID"
              getComponent={(next, done) => {
                System.import(
                  /* webpackChunkName: "StartPushbot" */ '../modules/x/StartPushbot'
                )
                  .then(loadRoute(done))
                  .catch(errorLoadingRoute);
              }}
            />
          )}

          <Route
            searchButton
            path="tasks"
            getComponent={(next, done) => {
              System.import(
                /* webpackChunkName: "Search" */ '../modules/x/Search'
              )
                .then(loadRoute(done))
                .catch(errorLoadingRoute);
            }}
          />

          {!workDoneBetterNavigation && <Redirect path="search*" to="tasks" />}
          <Route
            searchButton
            path="search"
            getComponent={(next, done) => {
              System.import(
                /* webpackChunkName: "Search" */ '../modules/x/Search'
              )
                .then(loadRoute(done))
                .catch(errorLoadingRoute);
            }}
          />

          <Redirect path="team" to="team/members" />
          <Route
            path="team/:tab(/:action)"
            getComponent={(next, done) => {
              System.import(/* webpackChunkName: "Team" */ '../modules/x/Team')
                .then(loadRoute(done))
                .catch(errorLoadingRoute);
            }}
          />

          <Route
            searchButton
            path="data"
            getComponent={(next, done) => {
              System.import(
                /* webpackChunkName: "Search" */ '../modules/x/Search'
              )
                .then(loadRoute(done))
                .catch(errorLoadingRoute);
            }}
          >
            {!manualReportingEnabled && <Redirect path="report" to="/data" />}
            <Route
              path="report"
              getComponent={(next, done) => {
                System.import(
                  /* webpackChunkName: "Report" */ '../modules/x/Report'
                )
                  .then(loadRoute(done))
                  .catch(errorLoadingRoute);
              }}
            />
            <Route
              path="import"
              getComponent={(next, done) => {
                System.import(
                  /* webpackChunkName: "ImportData" */ '../modules/x/DataTable/ImportData'
                )
                  .then(loadRoute(done))
                  .catch(errorLoadingRoute);
              }}
            />
          </Route>
          <Route
            path="data/tables/:tableID"
            getComponent={(next, done) => {
              System.import(
                /* webpackChunkName: "TableDetail" */ '../modules/x/DataTable/Detail'
              )
                .then(loadRoute(done))
                .catch(errorLoadingRoute);
            }}
          />
          <Route
            path="data/tables/:tableID/:menu"
            getComponent={(next, done) => {
              System.import(
                /* webpackChunkName: "TableDetail" */ '../modules/x/DataTable/Detail'
              )
                .then(loadRoute(done))
                .catch(errorLoadingRoute);
            }}
          />
          <Route
            path="models"
            getComponent={(next, done) => {
              System.import(
                /* webpackChunkName: "EditPredictiveModel" */ '../modules/x/PredictiveModel/PredictiveModelList'
              )
                .then(loadRoute(done))
                .catch(errorLoadingRoute);
            }}
          >
            <Route
              path=":predictorID"
              getComponent={(next, done) => {
                System.import(
                  /* webpackChunkName: "EditPredictiveModel" */ '../modules/x/PredictiveModel/EditPredictiveModel'
                )
                  .then(loadRoute(done))
                  .catch(errorLoadingRoute);
              }}
            />
            <Route
              path=":predictorID/status"
              getComponent={(next, done) => {
                System.import(
                  /* webpackChunkName: "TrainingStatusDialog" */ '../components/x/PredictiveModel/TrainingStatusDialog'
                )
                  .then(loadRoute(done))
                  .catch(errorLoadingRoute);
              }}
            />
          </Route>

          {!timelineChart && <Redirect path="chart*" to="report" />}
          <Redirect path="chart" to="report" />
          <Redirect path="chart/:tableID" to="report" />
          <Route
            path="chart/:tableID/:type"
            getComponent={(next, done) => {
              System.import(/* webpackChunkName: "Timeline" */ '../Timeline')
                .then(loadRoute(done))
                .catch(errorLoadingRoute);
            }}
          />

          <Route
            path="triggers(/:triggerID)"
            getComponent={(next, done) => {
              System.import(
                /* webpackChunkName: "TriggerList" */ '../modules/x/TriggerList'
              )
                .then(loadRoute(done))
                .catch(errorLoadingRoute);
            }}
          />
          <Route
            path="build/:processID/triggers/:triggerID"
            getComponent={(next, done) => {
              System.import(
                /* webpackChunkName: "TriggerList" */ '../modules/x/TriggerList'
              )
                .then(loadRoute(done))
                .catch(errorLoadingRoute);
            }}
          />
          <Route
            path="files/:fileID/download"
            getComponent={(next, done) =>
              require.ensure([], require =>
                done(null, require('../modules/x/File/FileDownload').default)
              )
            }
          />
        </Route>
      </Route>
    </Route>
  );
}

BetaRouter.displayName = 'BetaRouter';



// WEBPACK FOOTER //
// ./js/routes/BetaRouter.js